import { useState, useEffect, useRef } from 'react';
import { CardWidget } from '@/components/ui/card-widget';
import { Button } from '@/components/ui/button';
import { X, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { fetchVideoPresignUrlAtom } from '@/state/unity';
import { useAtomValue, useSetAtom } from 'jotai';
import { extractVideoKey } from '@/utils/extractVideoKey';
import { selectedActivityAtom, setSelectedActivityAtom } from '@/features/activity';

interface VideoPlayerProps {
  className?: string;
}

export function VideoPlayer({
  className = '',
}: VideoPlayerProps) {
  const selectedActivity = useAtomValue(selectedActivityAtom);
  const setSelectedActivity = useSetAtom(setSelectedActivityAtom);
  const s3VideoUrl = selectedActivity?.s3VideoUrl || null;
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const fetchVideoUrl = useSetAtom(fetchVideoPresignUrlAtom);
  
  // Reset state when activity changes
  useEffect(() => {
    if (s3VideoUrl) {
      setIsPlaying(false);
      setCurrentTime(0);
      setVideoError(null);
    }
  }, [s3VideoUrl]);

  useEffect(() => {
    async function loadVideoUrl() {
      if (!s3VideoUrl) return;
      
      try {
        const videoKey = extractVideoKey(s3VideoUrl);
        if (!videoKey) {
          setVideoError('Invalid video URL');
          return;
        }
        
        const presignedUrl = await fetchVideoUrl({ videoKey });
        setVideoUrl(presignedUrl);
      } catch (error) {
        console.error('Error fetching video URL:', error);
        setVideoError('Failed to load video');
      }
    }

    loadVideoUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s3VideoUrl]);

  // Global mouse event listeners for smooth dragging
  useEffect(() => {
    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (isDragging && videoRef.current && duration) {
        const progressBar = document.querySelector('.progress-bar');
        if (progressBar) {
          const rect = progressBar.getBoundingClientRect();
          const clickX = event.clientX - rect.left;
          const percentage = Math.max(0, Math.min(1, clickX / rect.width));
          const newTime = percentage * duration;

          videoRef.current.currentTime = newTime;
          setCurrentTime(newTime);
        }
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, duration]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(console.error);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    setCurrentTime(video.currentTime);
    setDuration(video.duration);
  };

  const handleVideoError = () => {
    setVideoError('Failed to load video');
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !duration) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressBarMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    setIsDragging(true);
    handleSeek(event);
  };

  const handleProgressBarMouseMove = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (isDragging) {
      handleSeek(event);
    }
  };

  const handleProgressBarMouseUp = () => {
    setIsDragging(false);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    setSelectedActivity(null);
  };

  if (!s3VideoUrl) {
    return (
      <CardWidget className={`min-h-[150px] ${className}`}>
        <h3 className="text-lg font-semibold mb-2">Activity Video</h3>
        <p className="text-gray-600">Select an activity to view its video</p>
      </CardWidget>
    );
  }

  return (
    <CardWidget className={`${className} flex flex-col h-full`}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h3 className="text-lg font-semibold">Activity Video</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-col h-full space-y-4">
        {/* Video Player */}
        <div className="relative bg-black rounded-lg overflow-hidden flex-1 min-h-0">
          {videoError || !videoUrl ? (
            <div className="flex items-center justify-center h-full text-white">
              <div className="text-center">
                <p className="text-red-400 mb-2">{videoError}</p>
                <p className="text-sm text-gray-400">Video URL: {s3VideoUrl}</p>
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full object-cover"
              onTimeUpdate={handleTimeUpdate}
              onError={handleVideoError}
              muted={isMuted}
              controls={false}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  setDuration(videoRef.current.duration);
                }
              }}
            />
          )}

          {/* Video Controls Overlay */}
          {!videoError && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              {/* Progress Bar */}
              <div className="mb-3">
                <div
                  className="progress-bar w-full h-1 bg-white/30 rounded-full cursor-pointer relative"
                  onMouseDown={handleProgressBarMouseDown}
                  onMouseMove={handleProgressBarMouseMove}
                  onMouseUp={handleProgressBarMouseUp}
                  onMouseLeave={handleProgressBarMouseUp}
                >
                  <div
                    className="h-full bg-white rounded-full transition-all duration-150"
                    style={{
                      width:
                        duration > 0
                          ? `${(currentTime / duration) * 100}%`
                          : '0%',
                    }}
                  />
                  <div
                    className="absolute top-1/2 w-3 h-3 bg-white rounded-full transform -translate-y-1/2 -translate-x-1/2 transition-all duration-150 hover:scale-110"
                    style={{
                      left:
                        duration > 0
                          ? `${(currentTime / duration) * 100}%`
                          : '0%',
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-white">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePlayPause}
                    className="h-8 w-8 p-0 text-white hover:bg-white/20"
                  >
                    {isPlaying ? (
                      <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMuteToggle}
                    className="h-8 w-8 p-0 text-white hover:bg-white/20"
                  >
                    {isMuted ? (
                      <VolumeX className="h-4 w-4" />
                    ) : (
                      <Volume2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <div className="text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </CardWidget>
  );
}

